import { useCallback, useState } from "react";

import { ClientGateway } from "@kex/api";

import { untilFromSeconds } from "@shared/libs/date-time";

import { getResendBlockingTime } from "../utils";

type Props = {
  initialOtp: ClientGateway.OtpModel;
  resend: (otp: ClientGateway.OtpModel) => Promise<ClientGateway.OtpModel>;
};

/**
 * OTP-сессия оверлея: текущая модель и дедлайн блокировки повторной отправки.
 * Дедлайн - единственный источник правды для таймера, он обновляется из двух мест:
 * resendBlockingTime успешного ответа и payload ошибки ErrOtpResendBlocked.
 * Остаток 0 означает, что блокировки нет и кнопка активна.
 */
export const useOtpSession = ({ initialOtp, resend }: Props) => {
  const [otp, setOtp] = useState(initialOtp);
  const [blockedUntil, setBlockedUntil] = useState(() =>
    untilFromSeconds(initialOtp.resendBlockingTime),
  );

  const blockFor = useCallback((seconds: number) => {
    setBlockedUntil(untilFromSeconds(seconds));
  }, []);

  const onResend = useCallback(async () => {
    try {
      const nextOtp = await resend(otp);
      setOtp(nextOtp);
      blockFor(nextOtp.resendBlockingTime);
    } catch (error) {
      // Таймер стартует заново только на 200. На ErrOtpResendBlocked берем остаток
      // из payload: 0 оставляет кнопку активной. Текст ошибки покажет глобальный тост.
      const seconds = getResendBlockingTime(error);

      if (seconds !== null) {
        blockFor(seconds);
      }
    }
  }, [blockFor, otp, resend]);

  return { blockedUntil, otp, onResend };
};
